<template>
  <div
    id="cookieConsentModal"
    class="cookie-consent-modal"
    :class="{ 'is-visible': isModalVisible, 'locale-zh-cn': isZhCn }"
  >
    <div class="cookie-consent-content">
      <!-- Title -->
      <h2 class="cookie-consent-title">
        {{ $t("cookieConsent.title") }}
      </h2>

      <!-- Description - 使用 v-html 渲染含連結的內容 -->
      <div 
        class="cookie-consent-description"
        v-html="isSettingsExpanded ? $t('cookieConsent.desc1') : $t('cookieConsent.description')"
        @click="handleDescriptionClick"
      ></div>

      <!-- Settings Panel - 僅在 Feature Flag 開啟且展開時顯示 -->
      <CookieSettingsPanel 
        v-if="showSettings && isSettingsExpanded"
        ref="settingsPanel"
      />

      <!-- Description - 結尾段落（展開時顯示在類別下方） -->
      <p 
        v-if="isSettingsExpanded"
        class="cookie-consent-description last-paragraph"
        v-html="$t('cookieConsent.desc3')"
        @click="handleDescriptionClick"
      ></p>

      <!-- Action Buttons -->
      <div class="cookie-consent-buttons" :class="{ 'settings-mode': isSettingsExpanded }">
        <!-- Back Button - 展開時顯示在最左邊 -->
        <button 
          v-if="isSettingsExpanded"
          class="back-btn"
          @click="goBackToCookieSettings"
        >
          {{ $t('cookieConsent.btnBackToCookieSettings') }}
        </button>

        <!-- Accept All Button - Feature Flag 控制 (最左邊) -->
        <div class="cookie-consent-buttons-primary" v-if="showAcceptAllButton">
          <button 
            class="accept-all-btn"
            @click="handleAcceptAll"
          >
            {{ $t("cookieConsent.btnAcceptAll") }}
          </button>
        </div>

        <!-- Accept Necessary Only Button - Feature Flag 控制 -->
        <button 
          v-if="showNecessaryOnlyButton && !isSettingsExpanded"
          class="necessary-only-btn"
          @click="handleNecessaryOnly"
        >
          {{ $t("cookieConsent.btnNecessaryOnly") }}
        </button>

        <!-- Cookie Settings Button - Feature Flag 控制 -->
        <button 
          v-if="showSettingsButton && !isSettingsExpanded"
          class="settings-btn"
          @click="toggleSettings"
        >
          {{ $t("cookieConsent.btnSettings") }}
        </button>

        <!-- 儲存並關閉按鈕 - 展開時顯示 -->
        <button 
          v-if="isSettingsExpanded"
          class="save-and-close-btn"
          @click="handleSaveAndClose"
        >
          {{ $t('cookieConsent.btnSaveAndClose') }}
        </button>

        <!-- Original Accept Button - Feature Flag 全關時顯示，點擊 = 全部接受 -->
        <button 
          v-if="!showAcceptAllButton"
          id="acceptCookiesButton"
          class="accept-btn"
          @click="handleAccept"
        >
          {{ $t("cookieConsent.btnAccept") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { COOKIE_FEATURES } from '@/config/cookieFeatureFlags';
import CookieSettingsPanel from './CookieSettingsPanel.vue';

export default {
  name: 'CookieConsentModal',
  
  components: {
    CookieSettingsPanel
  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const { locale } = useI18n();

    const isZhCn = computed(() => locale.value === 'ZH_CN');

    // Directly bind Vuex state to computed - no watcher needed
    const isModalVisible = computed(() => store.state.cookieConsent?.isModalVisible ?? false);
    const isSettingsExpanded = computed(() => store.state.cookieConsent?.isSettingsExpanded ?? false);

    // Feature Flag controls
    const showAcceptAllButton = computed(() => COOKIE_FEATURES.SHOW_ACCEPT_ALL);
    const showNecessaryOnlyButton = computed(() => COOKIE_FEATURES.SHOW_NECESSARY_ONLY);
    const showSettingsButton = computed(() => COOKIE_FEATURES.SHOW_SETTINGS);
    const showSettings = computed(() => COOKIE_FEATURES.SHOW_SETTINGS);

    // Actions
    const handleAcceptAll = () => {
      if (isSettingsExpanded.value) {
        // Manage Settings mode: only toggle all switches on, don't close
        settingsPanel.value?.selectAllToggles();
      } else {
        // Cookie Settings mode: accept all and close (original behavior)
        store.dispatch('cookieConsent/acceptAll');
      }
    };
    const handleNecessaryOnly = () => store.dispatch('cookieConsent/acceptNecessaryOnly');
    const handleAccept = () => store.dispatch('cookieConsent/acceptAll');
    const toggleSettings = () => store.dispatch('cookieConsent/toggleSettings');
    const goBackToCookieSettings = () => store.dispatch('cookieConsent/collapseToCookieSettings');

    // Navigation - 使用 router 導航到 privacy 頁面
    const goToPrivacy = () => router.push({ name: 'privacy' });

    // 呼叫 Settings Panel 的 handleSave()（與儲存設定相同），然後關閉
    const settingsPanel = ref(null);

    const handleSaveAndClose = () => {
      settingsPanel.value?.handleSave();
      store.dispatch('cookieConsent/hideModal');
    };

    // 點擊文內連結的委派處理
    const handleDescriptionClick = (e) => {
      const link = e.target.closest('a');
      if (!link) return;
      
      const action = link.dataset.action;
      if (action === 'settings') {
        e.preventDefault();
        toggleSettings();
      } else if (action === 'privacy') {
        e.preventDefault();
        goToPrivacy();
      }
    };

    return {
      isModalVisible,
      isSettingsExpanded,
      isZhCn,
      showAcceptAllButton,
      showNecessaryOnlyButton,
      showSettingsButton,
      showSettings,
      settingsPanel,
      handleAcceptAll,
      handleNecessaryOnly,
      handleAccept,
      toggleSettings,
      goBackToCookieSettings,
      handleSaveAndClose,
      handleDescriptionClick
    };
  }
};
</script>

<style scoped>
/* Scoped styles - only for component-specific overrides */
/* Global styles (cookieConsent.css) handle visibility, positioning, and layout */
</style>